import { Component, computed, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormBuilder } from '@angular/forms';
import { disabled, form, FormField, required } from '@angular/forms/signals';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService } from 'xng-breadcrumb';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import { MeasurementUnitEnum } from '@libs/types/src/common/enum/measurement-unit.enum';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { ServiceTypeEnum } from '@libs/types/src/services/enum/service-type.enum';
import { SocialServiceOperationEnum } from '@libs/types/src/services/enum/social-service-operation.enum';
import { ServicePatternStructureUsageEnum } from '@libs/types/src/services/enum/service-pattern-structure-usage.enum';
import { DefaultCategoryTypeEnum } from '@libs/types/src/file-manager/types/default-category.type';
import { FileKindEnum } from '@libs/types/src/file-manager/enum/file-kind.enum';
import { ExternalType } from '@libs/types/src/file-manager/enum/external-type.enum';
import { CreateWorkInput } from '@libs/types/src/services/input-type/work/create-work.input';
import { UpdateWorkInput } from '@libs/types/src/services/input-type/work/update-work.input';
import { WorkGridItemObject, WorkOfferObject } from '@libs/types/src/services/object-type/work/work-grid.object';
import {
    WorkDataOperationalWorkObject,
} from '@libs/types/src/services/object-type/work/work-data-operational-work.object';
import { ActionButtonsComponent } from '../../../kit/action-buttons/action-buttons.component';
import { DateInputComponent } from '../../../kit/input/date-input-v2/date-input.component';
import { NumberInputComponent } from '../../../kit/input/text-input-v2/number-input.component';
import { TextInputComponent } from '../../../kit/input/text-input-v2/text-input.component';
import { FileUploadBoxComponent } from '../../../kit/file-upload-box/file-upload-box.component';
import { UsersSelectComponent } from '../../../user-management/users-select/users-select.component';
import { ButtonComponent } from '../../../kit/button/button.component';
import { SplitterComponent } from '../../../kit/splitter/splitter.component';
import {
    ServicePatternWysiwygComponent,
} from '../../../../modules/content/components/common/service-pattern-wysiwyg/service-pattern-wysiwyg.component';
import {
    WorkCardContentComponent,
} from '../../../../modules/content/components/services/work-card-content/work-card-content.component';
import {
    WorkDetailSelectServiceComponent,
} from '../../../medical-service/work/work-detail/work-detail-select-service/work-detail-select-service.component';
import {
    SocialServiceSelectOfferComponent,
} from '../social-service-select-offer/social-service-select-offer.component';
import {
    SocialServiceOfferSelection,
    SocialServiceUnassignedOperationsConfig,
} from '../social-service-select-offer/social-service-select-offer.types';
import { checkOk } from '../../../../modules/content/content.error';
import { fb, goBack, routeParams } from '../../../../modules/shared/shared.global-service';
import { formAddText } from '../../../../utils/form.utils';
import { WorkOperationalWorkStore } from './work-operational-work.store';
import { operationalWorkDetailFrag } from './work-operational-work.query';
import { WorkOperationalWorkFormModel } from './work-operational-work.types';
import { SocialWorkPlanHistoryState } from '../social-works-plan/social-works-plan.types';

const EDUCATION_OPERATION_NAME = 'vzdělávání';

@Component({
    selector: 'app-work-operational-work',
    templateUrl: './work-operational-work.component.html',
    styleUrl: './work-operational-work.component.scss',
    providers: [WorkOperationalWorkStore],
    imports: [
        ActionButtonsComponent,
        ButtonComponent,
        DateInputComponent,
        FileUploadBoxComponent,
        FormField,
        NgTemplateOutlet,
        NumberInputComponent,
        ServicePatternWysiwygComponent,
        SocialServiceSelectOfferComponent,
        SplitterComponent,
        TextInputComponent,
        UsersSelectComponent,
        WorkCardContentComponent,
        WorkDetailSelectServiceComponent,
    ],
})
export class WorkOperationalWorkComponent {
    protected readonly store = inject(WorkOperationalWorkStore);
    private readonly breadcrumbService = inject(BreadcrumbService);
    private readonly fb = inject(FormBuilder);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);

    protected readonly fileUpload = viewChild(FileUploadBoxComponent);
    protected readonly selectOfferComponent = viewChild(SocialServiceSelectOfferComponent);
    protected readonly ServicePatternStructureUsageEnum = ServicePatternStructureUsageEnum;
    protected readonly ServiceTypeEnum = ServiceTypeEnum;
    protected readonly SocialServiceOperationEnum = SocialServiceOperationEnum;
    protected readonly WorkTypeEnum = WorkTypeEnum;
    protected readonly offerAdditionalFilter = [[{
        column: 'operationName',
        operator: 'NIN',
        value: [EDUCATION_OPERATION_NAME],
    }]];
    protected readonly unassignedOperations: SocialServiceUnassignedOperationsConfig = {
        defaults: {
            unit: MeasurementUnitEnum.MINUTE,
            usualCount: 0,
            usualTime: 30,
        },
        additionalFilter: [[{
            column: 'name',
            operator: 'NIN',
            value: [EDUCATION_OPERATION_NAME],
        }]],
    };
    public readonly inDialog = input(false);
    public readonly closeCallback = output<void>();

    protected readonly existingWork = signal<WorkGridItemObject<WorkDataOperationalWorkObject> | null>(null);
    protected readonly preselectedOffers = signal<WorkOfferObject[]>([]);
    protected readonly useSplitter = signal(false);
    protected readonly formData = signal<WorkOperationalWorkFormModel>({
        serviceId: this.store.globalGlobalStore.loggedUserStore.selectedServiceId() || null,
        userIds: this.defaultUserIds(
            this.store.globalGlobalStore.loggedUserStore.selectedServiceId() || null,
        ),
        dateFrom: new Date(),
        dateTo: new Date(),
        duration: null,
        offers: [],
        name: '',
    });
    protected readonly formDef = form(this.formData, (schema) => {
        required(schema.serviceId);
        required(schema.userIds);
        required(schema.dateFrom);
        required(schema.offers);
        required(schema.name);

        disabled(schema.duration);
    });
    protected readonly descriptionForm = this.fb.group({
        description: fb.control<string>(''),
    });

    private readonly workId = computed(() => this.inDialog() ? null : Number(routeParams.workId()) || null);
    private readonly queryParams = toSignal(this.route.queryParams);

    private lastOfferSelection = '';
    private waitingForInitialOffers = false;
    private readonly planState = history.state?.calendarCorrelationId
        ? history.state as SocialWorkPlanHistoryState
        : null;

    constructor() {
        if (this.planState) {
            const dateFrom = new Date(this.planState.dateFrom);
            const dateTo = new Date(this.planState.dateTo);
            const offers = this.planState.offers.map((offer) => ({
                optionId: `offer:${offer.offerId}`,
                offerId: offer.offerId,
                operationId: null,
                applications: offer.applications,
                operations: offer.operations,
                procedures: offer.procedures,
            }));
            this.waitingForInitialOffers = true;
            this.lastOfferSelection = this.offerSelectionKey(offers);
            this.formData.set({
                serviceId: this.planState.serviceId,
                userIds: this.planState.userIds,
                dateFrom,
                dateTo,
                duration: Math.max(1, Math.round((dateTo.getTime() - dateFrom.getTime()) / 60_000)),
                offers,
                name: this.planState.name,
            });
            this.preselectedOffers.set(this.planState.offers);
            this.descriptionForm.patchValue({ description: this.planState.note });
        }

        effect(() => {
            const workId = this.workId();
            if (!workId) return;

            this.breadcrumbService.set('@id', `id|${workId}`);
            void this.loadWork(workId);
        });

        effect(() => {
            if (this.workId() || this.inDialog()) return;

            const params = this.queryParams() || {};
            const sourceWorkId = Number(params['split'] ?? params['copy']) || null;
            this.useSplitter.set(Boolean(params['split'] && sourceWorkId));
            if (sourceWorkId) {
                void this.loadWork(sourceWorkId, !params['split']);
            }
        });

        effect(() => {
            const { dateFrom, duration, dateTo } = this.formData();
            const nextDateTo = new Date(dateFrom);
            nextDateTo.setMinutes(nextDateTo.getMinutes() + (duration || 0));
            if (dateTo.getTime() === nextDateTo.getTime()) return;

            untracked(() => {
                this.formData.update((value) => ({
                    ...value,
                    dateTo: nextDateTo,
                }));
            });
        });
    }

    protected addPattern(content: string): void {
        formAddText(this.descriptionForm, 'description', content);
    }

    protected selectService(serviceId: number | null): void {
        // The selector emits an empty value before applying the preselected service.
        if (!serviceId) return;

        this.formData.update((value) => {
            if (value.serviceId === serviceId) return value;

            this.lastOfferSelection = '';
            this.waitingForInitialOffers = false;
            this.preselectedOffers.set([]);
            this.selectOfferComponent()?.clear();
            return {
                ...value,
                serviceId,
                userIds: this.defaultUserIds(serviceId),
                offers: [],
                duration: null,
                dateTo: new Date(value.dateFrom),
            };
        });
    }

    protected setOffers(offers: SocialServiceOfferSelection[]): void {
        if (this.waitingForInitialOffers) {
            if (!offers.length) return;
            this.waitingForInitialOffers = false;
        }

        const selectionKey = this.offerSelectionKey(offers);
        const selectionChanged = selectionKey !== this.lastOfferSelection;
        this.lastOfferSelection = selectionKey;

        this.formData.update((value) => ({
            ...value,
            offers,
            ...(selectionChanged ? {
                duration: this.offersDuration(offers),
            } : {}),
        }));
    }

    protected existingFileIds(): number[] {
        if (!this.workId()) return [];

        return this.existingWork()?.files.map((file) => file.fileId) || [];
    }

    protected async save(): Promise<void> {
        const value = this.formData();
        if (
            this.formDef().invalid()
            || !value.serviceId
            || !value.duration
            || !value.offers.length
            || !value.userIds.length
            || !value.name.trim()
        ) return;

        const offers = await this.selectOfferComponent()?.resolveOffersForSave() || [];
        if (!offers.length) return;

        const fileIds = await this.fileUpload()?.uploadFiles({
            categoryCode: DefaultCategoryTypeEnum.OPERATIONAL_WORK,
            note: null,
            date: new Date(),
            kind: FileKindEnum.received,
            accesses: [{
                externalType: ExternalType.service,
                externalId: String(value.serviceId),
            }],
        }) || [];
        const commonData = {
            serviceId: value.serviceId,
            dateFrom: value.dateFrom.toISOString(),
            dateTo: value.dateTo.toISOString(),
            userIds: value.userIds,
            fileIds,
            offers,
            workData: JSON.stringify({
                name: value.name.trim(),
                description: this.descriptionForm.controls.description.value || null,
            }),
        };

        const workId = this.workId();
        if (workId) {
            const result = await this.store.api.updateWork({
                ...commonData,
                id: workId,
            } satisfies UpdateWorkInput);
            if (!checkOk(result, 'update')) return;
        } else {
            const result = await this.store.api.createWork({
                ...commonData,
                folderId: null,
                workType: WorkTypeEnum.OPERATIONAL_WORK,
                calendarCorrelationId: this.planState?.calendarCorrelationId,
            } satisfies CreateWorkInput);
            if (!checkOk(result, 'create')) return;
        }

        await this.fileUpload()?.confirmFiles(fileIds);
        this.close();
    }

    protected close(): void {
        if (this.inDialog()) {
            this.closeCallback.emit();
        } else if (this.planState?.returnUrl) {
            void this.router.navigateByUrl(this.planState.returnUrl);
        } else {
            goBack();
        }
    }

    private async loadWork(workId: number, patchForm = true): Promise<void> {
        const result = await this.store.api.getWorks(
            FilterBuilder.filterById(workId),
            operationalWorkDetailFrag,
        );
        const work = result.items?.[0] as WorkGridItemObject<WorkDataOperationalWorkObject> | undefined;
        if (!work) return;

        this.existingWork.set(work);
        if (!patchForm) return;

        const dateFrom = new Date(work.dateFrom);
        const dateTo = new Date(work.dateTo || work.dateFrom);
        const offers = work.offers.map((offer) => ({
            optionId: `offer:${offer.offerId}`,
            offerId: offer.offerId,
            operationId: null,
            applications: offer.applications,
            operations: offer.operations,
            procedures: offer.procedures,
        }));
        this.waitingForInitialOffers = true;
        this.lastOfferSelection = this.offerSelectionKey(offers);
        this.formData.set({
            serviceId: work.serviceId || null,
            userIds: work.users.map((user) => user.id),
            dateFrom,
            dateTo,
            duration: Math.max(1, Math.round((dateTo.getTime() - dateFrom.getTime()) / 60_000)),
            offers,
            name: work.workData?.name || '',
        });
        this.preselectedOffers.set(work.offers);
        this.descriptionForm.patchValue({
            description: work.workData?.description || '',
        });
    }

    private offerSelectionKey(offers: SocialServiceOfferSelection[]): string {
        return offers
            .map((offer) => `${offer.optionId}:${offer.applications}`)
            .sort()
            .join(',');
    }

    private offersDuration(offers: SocialServiceOfferSelection[]): number | null {
        const duration = offers.reduce((sum, offer) => sum + offer.applications, 0);

        return duration || null;
    }

    private defaultUserIds(serviceId: number | null): string[] {
        const loggedUserStore = this.store.globalGlobalStore.loggedUserStore;
        const currentUserId = loggedUserStore.currentUserId();
        if (!serviceId || !currentUserId) return [];

        return loggedUserStore.employments().some((employment) => employment.serviceId === serviceId)
            ? [currentUserId]
            : [];
    }
}
